// طباعة ملصق باركود واحد على طابعة الكاشير الحرارية 78مم.
//
// نولّد صورة الباركود عبر JsBarcode في canvas مخفي ثم نحوّلها إلى dataURL
// ونضعها داخل iframe بأنماط مدمجة inline (نفس أسلوب printReceipt.js).
//
// المدخلات:
//   value: قيمة الباركود (EAN-13 افتراضي، يقبل أنواع أخرى كـ CODE128)
//   label: اسم المنتج (يطبع فوق الباركود)
//   sku:   الـ SKU (اختياري)
//   price: السعر بـ IQD (اختياري)
//   format: 'EAN13' (افتراضي) أو 'CODE128' للقيم غير الرقمية

import JsBarcode from 'jsbarcode';
import { formatIQD } from './format.js';

const PRINT_CSS = `
/* ورق 80mm فعلي مع هامش أمان من الجانبين (الطابعات الحرارية تقص الحواف). */
@page { size: 80mm auto; margin: 0; }
html, body {
  margin: 0;
  padding: 0;
  background: #fff;
  color: #000;
}
body {
  width: 80mm;
  font-family: 'Tajawal', 'Cairo', sans-serif;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.barcode-label {
  width: 76mm;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 4mm 3mm;
  text-align: center;
  color: #000;
  background: #fff;
}
.barcode-label .product-name {
  font-size: 16px;
  font-weight: 800;
  margin-bottom: 4px;
  line-height: 1.3;
  word-wrap: break-word;
}
.barcode-label .meta {
  font-size: 12px;
  color: #000;
  margin-bottom: 6px;
}
.barcode-label .price {
  font-size: 20px;
  font-weight: 900;
  margin-top: 4px;
}
.barcode-label img.barcode-img {
  display: block;
  margin: 6px auto 0;
  max-width: 70mm;
  height: auto;
  image-rendering: crisp-edges;
}
`;

function detectFormat(value) {
  const s = String(value || '').trim();
  if (/^\d{13}$/.test(s)) return 'EAN13';
  if (/^\d{12}$/.test(s)) return 'UPC';
  if (/^\d{8}$/.test(s)) return 'EAN8';
  return 'CODE128';
}

function renderBarcodeDataUrl({ value, format }) {
  const canvas = document.createElement('canvas');
  try {
    JsBarcode(canvas, String(value), {
      format,
      width: 2,
      height: 60,
      displayValue: true,
      fontSize: 14,
      textMargin: 2,
      margin: 4,
      background: '#ffffff',
      lineColor: '#000000'
    });
    return canvas.toDataURL('image/png');
  } catch (err) {
    // قيمة لا تتوافق مع EAN13 — نُعيد المحاولة كـ CODE128
    try {
      JsBarcode(canvas, String(value), {
        format: 'CODE128',
        width: 2,
        height: 60,
        displayValue: true,
        fontSize: 14,
        textMargin: 2,
        margin: 4,
        background: '#ffffff',
        lineColor: '#000000'
      });
      return canvas.toDataURL('image/png');
    } catch (err2) {
      return null;
    }
  }
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildHtml({ imgSrc, label, sku, price }) {
  const parts = [];
  parts.push(`<div class="barcode-label">`);
  if (label) parts.push(`<div class="product-name">${escapeHtml(label)}</div>`);
  if (sku) parts.push(`<div class="meta">SKU: ${escapeHtml(sku)}</div>`);
  parts.push(`<img class="barcode-img" src="${imgSrc}" alt="barcode" />`);
  if (price != null && Number(price) > 0) {
    parts.push(`<div class="price">${escapeHtml(formatIQD(Number(price)))}</div>`);
  }
  parts.push(`</div>`);
  return (
    `<!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="utf-8">` +
    `<style>${PRINT_CSS}</style></head><body>${parts.join('')}</body></html>`
  );
}

function waitForImages(doc) {
  const imgs = Array.from(doc.querySelectorAll('img'));
  if (imgs.length === 0) return Promise.resolve();
  return Promise.all(
    imgs.map((img) => {
      if (img.complete && img.naturalWidth !== 0) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => resolve();
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      });
    })
  );
}

async function printIframe(html) {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.left = '-10000px';
  iframe.style.top = '0';
  iframe.style.width = '80mm';
  iframe.style.height = '100mm';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  iframe.srcdoc = html;
  document.body.appendChild(iframe);

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    try {
      iframe.remove();
    } catch (_) {
      /* ignore */
    }
  };

  const safetyCleanup = setTimeout(cleanup, 60000);

  await new Promise((resolve) => {
    if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
      resolve();
    } else {
      iframe.addEventListener('load', () => resolve(), { once: true });
    }
  });

  const win = iframe.contentWindow;
  const doc = iframe.contentDocument;
  if (!win || !doc) {
    clearTimeout(safetyCleanup);
    cleanup();
    return;
  }

  try {
    if (doc.fonts && typeof doc.fonts.ready?.then === 'function') {
      await doc.fonts.ready;
    }
  } catch (_) {
    /* ignore */
  }

  try {
    await waitForImages(doc);
  } catch (_) {
    /* ignore */
  }

  win.addEventListener('afterprint', () => {
    clearTimeout(safetyCleanup);
    cleanup();
  });

  try {
    win.focus();
    win.print();
  } catch (_) {
    clearTimeout(safetyCleanup);
    cleanup();
  }
}

export function printBarcode({ value, label, sku, price, format } = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  const v = String(value || '').trim();
  if (!v) return false;
  const fmt = format || detectFormat(v);
  const imgSrc = renderBarcodeDataUrl({ value: v, format: fmt });
  if (!imgSrc) return false;
  const html = buildHtml({ imgSrc, label, sku, price });
  void printIframe(html);
  return true;
}
